import { h } from '../dom';
import type { ScreenCtx, HalfTimeParams } from './context';
import { categoryBars, letterBadge, listNav, statBlock, type ScreenInstance } from './common';
import { CATEGORY_HINTS, CATEGORY_LABELS, formatCompact } from '../format';
import { CATEGORY_ORDER } from '../../meta/share';

export function pauseScreen(ctx: ScreenCtx): ScreenInstance {
  let quitArmed = false;
  const last = ctx.lastStart();
  const btn = (label: string, key: string | null, cls: string, fn: () => void) =>
    h('button', { class: `sd-btn sd-pause__btn ${cls}`, attrs: { type: 'button' }, on: { click: fn, mouseenter: (e) => (e.currentTarget as HTMLElement).focus({ preventScroll: true }) } }, [h('span', { text: label }), key ? h('kbd', { text: key }) : null]);
  const quit = btn('QUIT TO MENU', null, 'sd-btn--danger', () => {
    if (!quitArmed) { quitArmed = true; quit.firstChild!.textContent = 'QUIT? MATCH WILL BE LOST'; return; }
    ctx.deps.onQuit?.();
    ctx.show('title');
  });
  const list = h('div', { class: 'sd-pause__list' }, [
    btn('RESUME', 'ESC', 'sd-btn--primary', () => { ctx.show('none'); ctx.deps.onResume?.(); }),
    btn('RESTART MATCH', null, '', () => { ctx.show('none'); if (ctx.deps.onRestart) ctx.deps.onRestart(); else ctx.retry(); }),
    btn('SETTINGS', null, '', () => ctx.show('settings', { overlay: true, back: 'pause' })),
    btn('HOW TO PLAY', null, '', () => ctx.show('howto', { back: 'pause' })),
    quit,
  ]);
  const el = h('section', { class: 'sd-screen sd-screen--overlay sd-pause' }, [
    h('div', { class: 'sd-pause__card' }, [
      h('div', { class: 'sd-pause__stripe' }),
      h('div', { class: 'sd-pause__kicker', text: last ? last.config.competition.toUpperCase() : 'MATCH' }),
      h('h1', { class: 'sd-pause__title', text: 'PAUSED' }),
      h('div', { class: 'sd-pause__sub', text: 'Feeds frozen. The crowd can wait.' }),
      list,
    ]),
  ]);
  const buttons = () => Array.from(list.querySelectorAll<HTMLElement>('button'));
  return {
    el, overlay: true,
    mounted() { buttons()[0]?.focus({ preventScroll: true }); },
    onKey(e) {
      if (e.key === 'Escape') { ctx.show('none'); ctx.deps.onResume?.(); return true; }
      return listNav(buttons, e);
    },
  };
}

export function halftimeScreen(ctx: ScreenCtx, p: HalfTimeParams): ScreenInstance {
  const r = p.report;
  const weakest = [...CATEGORY_ORDER].sort((a, b) => (r.byCategory[a] ?? 0) - (r.byCategory[b] ?? 0))[0]!;
  const covered = r.keyMoments.filter((k) => k.covered).length;
  const go = () => { ctx.show('none'); ctx.deps.onSecondHalf?.(); };
  const el = h('section', { class: 'sd-screen sd-screen--overlay sd-ht' }, [
    h('div', { class: 'sd-ht__card' }, [
      h('div', { class: 'sd-ht__banner' }, [h('span', { text: 'HALF-TIME' })]),
      h('div', { class: 'sd-ht__score' }, [
        h('span', { class: 'sd-ht__team', style: p.homeColor ? `--tc:${p.homeColor}` : '', text: p.homeName }),
        h('span', { class: 'sd-ht__n', text: `${p.score.home}` }),
        h('span', { class: 'sd-ht__sep', text: '–' }),
        h('span', { class: 'sd-ht__n', text: `${p.score.away}` }),
        h('span', { class: 'sd-ht__team', style: p.awayColor ? `--tc:${p.awayColor}` : '', text: p.awayName }),
      ]),
      h('div', { class: 'sd-ht__body' }, [
        h('div', { class: 'sd-ht__grade' }, [
          letterBadge(r.letter, 'sd-letter--xl'),
          h('div', { class: 'sd-ht__rating', text: `${Math.round(r.rating)}` }),
          h('div', { class: 'sd-field__label', text: 'FIRST-HALF RATING' }),
        ]),
        categoryBars(r.byCategory),
        h('div', { class: 'sd-ht__stats' }, [
          statBlock('VIEWERS', formatCompact(r.viewers)),
          statBlock('KEY MOMENTS', `${covered}/${r.keyMoments.length}`, 'covered'),
          statBlock('POINTS', Math.round(r.total).toLocaleString('en-GB')),
        ]),
      ]),
      h('div', { class: 'sd-ht__tip' }, [h('span', { class: 'sd-ht__tiplabel', text: `NOTE FROM THE GALLERY · ${CATEGORY_LABELS[weakest]}` }), h('span', { text: CATEGORY_HINTS[weakest] })]),
      h('button', { class: 'sd-btn sd-btn--primary sd-btn--golive', attrs: { type: 'button' }, on: { click: go } }, [h('span', { class: 'sd-golive__dot' }), 'START SECOND HALF', h('kbd', { text: 'SPACE' })]),
    ]),
  ]);
  return {
    el, overlay: true,
    mounted() { (el.querySelector('.sd-btn--golive') as HTMLElement | null)?.focus({ preventScroll: true }); },
    onKey(e) { if (e.code === 'Space' || e.key === 'Enter') { go(); return true; } return false; },
  };
}
